import { Trash2, Check, Clock, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from './constants';

export default function TruffleOrdersTab({ orders, onRefresh }) {
  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/truffle-orders/${orderId}?status=${status}`);
      toast.success(`Order marked as ${status}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await axios.delete(`${API}/admin/truffle-orders/${id}`);
      toast.success('Order deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const completedOrders = orders.filter(o => o.status !== 'pending');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Truffle Orders ({orders.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No truffle orders yet</p>
        ) : (
          <div className="space-y-6">
            {pendingOrders.length > 0 && (
              <div>
                <h3 className="font-bold text-secondary mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Pending Orders ({pendingOrders.length})
                </h3>
                <div className="space-y-3">
                  {pendingOrders.map((order) => (
                    <OrderCard key={order.id} order={order} onUpdateStatus={updateStatus} onDelete={deleteOrder} />
                  ))}
                </div>
              </div>
            )}
            {completedOrders.length > 0 && (
              <div>
                <h3 className="font-bold text-muted-foreground mb-3 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Completed ({completedOrders.length})
                </h3>
                <div className="space-y-3">
                  {completedOrders.map((order) => (
                    <OrderCard key={order.id} order={order} onUpdateStatus={updateStatus} onDelete={deleteOrder} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrderCard({ order, onUpdateStatus, onDelete }) {
  return (
    <div className={`p-4 rounded-lg border ${order.status === 'pending' ? 'bg-secondary/5 border-secondary/30' : 'bg-muted/30 border-border'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-bold">{order.customer_name}</h4>
            <span className={`text-xs px-2 py-0.5 rounded ${order.status === 'pending' ? 'bg-secondary/20 text-secondary' : 'bg-green-500/20 text-green-500'}`}>
              {order.status}
            </span>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
            <Mail className="w-3 h-3" /> {order.customer_email}
          </p>
          <div className="mt-2 text-sm">
            <p><strong>Package:</strong> {order.package_name}</p>
            <p><strong>Qty:</strong> {order.quantity} • <strong>Total:</strong> ${order.total_price?.toFixed(2)}</p>
            {order.shipping_address && (
              <p className="flex items-start gap-1 mt-1">
                <MapPin className="w-3 h-3 mt-0.5" />
                <span>{order.shipping_address}</span>
              </p>
            )}
            {order.special_requests && (
              <p className="text-muted-foreground mt-1">Requests: {order.special_requests}</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Ordered: {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {order.status === 'pending' && (
            <Button size="sm" variant="outline" className="gap-1" onClick={() => onUpdateStatus(order.id, 'completed')}>
              <Check className="w-3 h-3" /> Complete
            </Button>
          )}
          <Button size="sm" variant="destructive" onClick={() => onDelete(order.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
